import { useEffect, useRef, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { logout } from "../../redux/actions/auth";
import { CLEAR_FORM_STATE } from "../../redux/actions/applicationType";

const INACTIVITY_LIMIT = 30 * 60 * 1000;
const WARNING_SECONDS = 60;
const MAX_SESSION = 1 * 60 * 60 * 1000;
const SESSION_KEYS = ["LOGIN_TIME", "LAST_ACTIVITY", "LOGOUT_TRIGGER"];

export default function SessionTimeout({ onLogout }) {
  const { isLoggedIn } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const location = useLocation();

  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(WARNING_SECONDS);
  const [isLoggedOut, setIsLoggedOut] = useState(false);

  const inactivityTimer = useRef(null);
  const warningInterval = useRef(null);
  const maxSessionTimer = useRef(null);
  const bc = useRef(null);

  useEffect(() => {
    if (!isLoggedIn || isLoggedOut) {
      cleanupAll();
      return;
    }

    bc.current = new BroadcastChannel("session_channel");
    initSession();
    setupListeners();

    return cleanupAll;
  }, [isLoggedIn, isLoggedOut]);

  const initSession = () => {
    const now = Date.now();

    if (!localStorage.getItem("LOGIN_TIME")) {
      localStorage.setItem("LOGIN_TIME", now);
    }
    if (!localStorage.getItem("LAST_ACTIVITY")) {
      localStorage.setItem("LAST_ACTIVITY", now);
    }

    startInactivityTimer();
    startMaxSessionTimer();
  };

  const startInactivityTimer = () => {
    clearTimeout(inactivityTimer.current);

    const lastActivity = Number(localStorage.getItem("LAST_ACTIVITY")) || Date.now();
    const elapsed = Date.now() - lastActivity;
    const remaining = Math.max(INACTIVITY_LIMIT - elapsed, 0);

    inactivityTimer.current = setTimeout(() => {
      if (!isLoggedOut) startWarning();
    }, remaining);
  };

  const startMaxSessionTimer = () => {
    clearTimeout(maxSessionTimer.current);

    const loginTime = Number(localStorage.getItem("LOGIN_TIME")) || Date.now();
    const elapsed = Date.now() - loginTime;
    const remaining = Math.max(MAX_SESSION - elapsed, 0);

    if (remaining <= 0) {
      forceLogout();
      return;
    }

    maxSessionTimer.current = setTimeout(() => {
      if (!isLoggedOut) forceLogout();
    }, remaining);
  };

  const startWarning = () => {
    if (isLoggedOut || !isLoggedIn) return;
    setShowWarning(true);
    setCountdown(WARNING_SECONDS);

    clearInterval(warningInterval.current);
    warningInterval.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(warningInterval.current);
          forceLogout();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const continueSession = () => {
    if (isLoggedOut) return;

    setShowWarning(false);
    clearInterval(warningInterval.current);
    localStorage.setItem("LAST_ACTIVITY", Date.now());
    startInactivityTimer();
  };

  const handleUserActivity = () => {
    if (isLoggedOut || showWarning) return;

    localStorage.setItem("LAST_ACTIVITY", Date.now());
    startInactivityTimer();
  };

  const setupListeners = () => {
    ["mousemove", "click", "keydown", "scroll", "touchstart"].forEach((e) =>
      window.addEventListener(e, handleUserActivity)
    );

    window.addEventListener("storage", onStorageEvent);

    bc.current.onmessage = (msg) => {
      if (msg.data === "logout") forceLogout(false);
    };
  };

  const onStorageEvent = (e) => {
    if (e.key === "LOGOUT_TRIGGER") {
      forceLogout(false);
    }
  };

  const forceLogout = (broadcast = true) => {
    if (isLoggedOut) return;

    setIsLoggedOut(true);
    setShowWarning(false);

    clearTimeout(inactivityTimer.current);
    clearTimeout(maxSessionTimer.current);
    clearInterval(warningInterval.current);

    if (broadcast) {
      bc.current?.postMessage("logout");
      localStorage.setItem("LOGOUT_TRIGGER", Date.now());
    }

    clearSession();

    // ❗Important: Delay logout dispatch slightly
    setTimeout(() => {
      dispatch({ type: CLEAR_FORM_STATE });
      dispatch(logout());
      onLogout?.();
    }, 0);
  };

  const clearSession = () => {
    SESSION_KEYS.forEach((k) => localStorage.removeItem(k));
  };

  /* -------------------- CLEANUP -------------------- */
  const cleanupAll = () => {
    ["mousemove", "click", "keydown", "scroll", "touchstart"].forEach((e) =>
      window.removeEventListener(e, handleUserActivity)
    );

    window.removeEventListener("storage", onStorageEvent);

    clearTimeout(inactivityTimer.current);
    clearTimeout(maxSessionTimer.current);
    clearInterval(warningInterval.current);

    try {
      bc.current?.close();
    } catch {
      /* empty */
    }
  };

  if (isLoggedOut) return null;

  /* -------------------- UI -------------------- */
  return (
    <Modal show={showWarning} centered backdrop="static">
      <Modal.Header>
        <Modal.Title>Session Timeout Warning</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        You will be logged out in <b>{countdown}</b> seconds due to inactivity.
      </Modal.Body>

      <Modal.Footer>
        <Button variant="primary" onClick={continueSession}>
          Continue Session
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
